import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaPaperPlane, FaComment, FaEllipsisV, FaTrash, FaCircle } from 'react-icons/fa';
import '../../../styles/ChatSidebar.css';
import { useBandChat } from '../../../hooks/useBandChat.js';
import { isBoostLineHeld } from '../../../hooks/useFxBoosts.js';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { isSignedIn, rememberReturnPath } from '../../../_helpers/safeRedirect.js';
import { NAME_PAINT_SURFACES } from '../../../_helpers/namePaint.js';
import PaintedName from './PaintedName.jsx';
import ListenerProfileCard from './ListenerProfileCard.jsx';

/** Names of who is typing as a locale-aware list ("a, b, c" in English — the unit/short style has no "and"). */
const formatTypingUsers = (users, format) => format.list(users, { style: 'short', type: 'unit' });

/** Username colours of the stream variant (mockup 01, ENG-667), picked per name so a viewer keeps theirs. */
export const USERNAME_COLOR_COUNT = 8;
export const usernameColorIndex = (name) => {
  let hash = 0;
  for (const ch of String(name || '')) hash = (hash * 31 + ch.codePointAt(0)) >>> 0;
  return hash % USERNAME_COLOR_COUNT;
};

/** "8:05 PM" in the active locale; '' when the value is missing or unparseable. */
const formatChatTimestamp = (value, format) => format.time(value);

// SongShake chat is the only stream chat, regardless of which video source
// (Twitch, YouTube, SongShake) the band streams from.
//
// `variant="cockpit"` is the streamer dashboard's audience-center styling
// (ENG-359): the panel supplies its own "Chat" tab, so the header is dropped
// and the composer becomes the pill input + "Send" button from the design.
//
// `isLive` is the band's stream status: chat only exists during a live show,
// so while it is false the chat stays idle and the composer is off (ENG-480).
// The composer's stand-in for a signed-out visitor (ENG-503). Its own
// component so that only it needs the router.
const ChatSignInPrompt = () => {
  const { t } = useLocale();
  const navigate = useNavigate();
  return (
    <div className="chat-signin-prompt">
      <span>{t('chat.signIn.prompt')}</span>
      <button
        type="button"
        className="chat-signin-button"
        onClick={() => {
          rememberReturnPath();
          navigate('/login');
        }}
      >
        {t('common.actions.signIn')}
      </button>
    </div>
  );
};

/**
 * Hands `render` a function that opens the skins shop (/shop, ENG-823) with
 * `state.from` set to this page, for the shop's "Back to stream". Only mounted
 * with the stream page's profile card, so the other chat variants need no
 * router.
 */
const ShopNavigator = ({ render }) => {
  const navigate = useNavigate();
  const location = useLocation();
  return render(() => navigate('/shop', { state: { from: `${location.pathname}${location.search}` } }));
};

/**
 * A boost's chat line (mockup 07, ENG-679): "songshake: Sam boosted Hotel
 * California +$5 → now #4 (ahead of London Calling)", or "you boosted…" for
 * the booster. `still` is the booster's own line for a boost that passed
 * nobody (the server posts no public line for those).
 */
export const BoostChatLine = ({ boost, username, own = false, still = false }) => {
  const { t, format } = useLocale();
  const values = {
    name: username,
    title: boost.songTitle,
    amount: format.usd(boost.amountCents / 100, { cents: 'auto' }),
    spot: format.number(boost.spot),
    passed: boost.passedTitle,
  };
  let key = own ? 'stream.chat.boost.you' : 'stream.chat.boost.other';
  if (still) key = 'stream.chat.boost.youStill';
  else if (boost.passedTitle) key += 'Ahead';
  return (
    <div className="chat-line chat-line--boost" data-testid="chat-boost-line">
      <span className="chat-line-name chat-line-name--system">{t('stream.chat.systemName')}</span>
      <span className="chat-line-separator" aria-hidden="true">{t('chat.lineSeparator')}</span>
      <span className="chat-line-text">{t(key, values)}</span>
    </div>
  );
};

// `variant="stream"` is the listener stream page (mockup 01, ENG-667): no
// header, one line per message ("name: message") with a coloured name, a
// welcome system line, the `pinned` rows (tip highlights) above the composer,
// and a "Send a message" box with a CHAT button. Boost lines (ENG-679) render
// as songshake system lines, "you" for `ownUserId`'s own; `localLines` are
// lines only this viewer sees ({ id, timestamp, boost, still }). `banner` is
// pinned above the messages (the save-card banner, ENG-703). A name on a
// message line opens that listener's profile card over the chat (mockup 34,
// ENG-733). `onSeeWhatTheySee(card)` wires the card's "See what they see"
// (mockup 35, ENG-735); the card closes once it is used. `onTryOn(card)`
// wires its "Try it on" (mockup 36, ENG-737) the same way; `tryOnPanel` sits
// above the message box while a skin is on, and `ownNamePaint` (a tried-on
// name paint) paints this viewer's own lines, on their screen only.
// `onSeeAll(card)` wires "See all" on this viewer's own card to the skin shelf
// (mockup 37, ENG-739). The shelf is `GET /me/skins/shelf`, the viewer's own,
// so "See all" on another listener's card goes to the skins shop instead
// (/shop, ENG-823), with `state.from` set to this page for its "Back to
// stream". There is no public shelf of another listener's skins.
// `heldBoosts` ({entryId, at}[], ENG-808) are boosts still playing on the
// queue: their boost line waits until the animation ends (`boost-end`).
const ChatSidebar = ({ bandId, isVisible = true, isLive = true, canModerate = false, variant = 'default', pinned = null, ownUserId = null, localLines = null, banner = null, onSeeWhatTheySee = null, onTryOn = null, tryOnPanel = null, ownNamePaint = null, onSeeAll = null, heldBoosts = null }) => {
  const { t, format } = useLocale();
  const [message, setMessage] = useState('');
  // The open profile card: { userId, username, lineId }, or null.
  const [profileCard, setProfileCard] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const messagesContainerRef = useRef(null);
  const isCockpit = variant === 'cockpit';
  const isStream = variant === 'stream';

  const { messages, connectionStatus, sendMessage, retryMessage, discardMessage, deleteMessage, typingUsers, sendTypingIndicator } =
    useBandChat({ bandId, enabled: isLive });
  const isOffline = connectionStatus === 'offline';
  // Chat needs a session (ENG-503): signed out, the composer is a prompt to
  // sign in, which comes back to this page afterwards.
  const signedIn = isSignedIn();
  const ownCard = Boolean(profileCard) && ownUserId != null && profileCard.userId === Number(ownUserId);
  const paintOf = (msg) => (ownNamePaint && ownUserId != null && msg.userId === Number(ownUserId) ? ownNamePaint : msg.namePaint);

  // The stream variant interleaves this viewer's own lines by time.
  const streamLines = useMemo(() => {
    const at = (m) => new Date(m.timestamp).getTime() || 0;
    const all = !isStream || !localLines?.length
      ? messages
      : [...messages, ...localLines.map((line) => ({ ...line, local: true }))].sort((a, b) => at(a) - at(b));
    // A boost's line shows once its animation has ended (ENG-808).
    return isStream && heldBoosts?.length ? all.filter((m) => !isBoostLineHeld(heldBoosts, m)) : all;
  }, [isStream, messages, localLines, heldBoosts]);

  useEffect(() => {
    if (!messagesContainerRef.current) return;
    messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
  }, [streamLines]);

  const statusLabel = useMemo(() => {
    if (connectionStatus === 'connected') return t('common.status.connected');
    if (connectionStatus === 'reconnecting') return t('chat.connection.reconnecting');
    if (connectionStatus === 'polling') return t('chat.connection.offlineMode');
    if (connectionStatus === 'offline') return t('chat.connection.notLive');
    return t('chat.connection.disconnected');
  }, [connectionStatus, t]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const trimmed = message.trim();
    if (!trimmed) return;

    const result = await sendMessage(trimmed);
    if (result?.ok) {
      setMessage('');
    }
  };

  const handleMessageChange = (e) => {
    setMessage(e.target.value);
    sendTypingIndicator();
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  if (!isVisible) {
    return null;
  }

  const showBody = isCockpit || isStream || isExpanded;

  return (
    <div className={`chat-sidebar ${isExpanded ? 'expanded' : 'collapsed'}${isCockpit ? ' chat-sidebar--cockpit' : ''}${isStream ? ' chat-sidebar--stream' : ''}`}>
      {isCockpit || isStream ? (
        // No header in the cockpit; the connection state still needs a home
        // when the socket is not healthy.
        connectionStatus !== 'connected' && (
          <span className={`chat-connection-status chat-connection-status--${connectionStatus}`}>
            <FaCircle className="connection-dot" /> {statusLabel}
          </span>
        )
      ) : (
        <div className="chat-header">
          <div className="chat-header-info">
            <FaComment className="chat-icon" />
            <span className="chat-title">{t('chat.title')}</span>
            <span className={`chat-connection-status chat-connection-status--${connectionStatus}`}>
              <FaCircle className="connection-dot" /> {statusLabel}
            </span>
          </div>
          <button
            className="chat-toggle-button"
            onClick={toggleExpanded}
            title={isExpanded ? t('chat.collapse') : t('chat.expand')}
          >
            <FaEllipsisV />
          </button>
        </div>
      )}

      {showBody && (
        <>
          {isStream && banner}
          <div className="chat-messages" ref={messagesContainerRef}>
            <div className="chat-messages-container">
              {isStream && (
                <div className="chat-system-line" data-testid="chat-welcome">{t('chat.welcome')}</div>
              )}
              {streamLines.length === 0 && (
                <div className="chat-empty">{t('chat.empty')}</div>
              )}
              {isStream && streamLines.map((msg) => (msg.boost ? (
                <BoostChatLine
                  key={msg.id}
                  boost={msg.boost}
                  username={msg.username}
                  own={msg.local || (ownUserId != null && msg.userId === Number(ownUserId))}
                  still={Boolean(msg.still)}
                />
              ) : (
                <div
                  key={msg.id}
                  className={`chat-line${msg.failed ? ' chat-message--failed' : ''}${profileCard?.lineId === msg.id ? ' chat-line--selected' : ''}`}
                  data-testid="chat-line"
                >
                  {msg.userId > 0 ? (
                    <button
                      type="button"
                      className="chat-line-name-button"
                      aria-label={t('stream.chat.openCard', { name: msg.username })}
                      aria-haspopup="dialog"
                      aria-expanded={profileCard?.lineId === msg.id}
                      onClick={() => setProfileCard({ userId: msg.userId, username: msg.username, lineId: msg.id })}
                    >
                      <PaintedName
                        name={msg.username}
                        paint={paintOf(msg)}
                        surface={NAME_PAINT_SURFACES.chat}
                        className="chat-line-name"
                        fallbackClassName={`chat-line-name--c${usernameColorIndex(msg.username)}`}
                      />
                    </button>
                  ) : (
                    <PaintedName
                      name={msg.username}
                      paint={paintOf(msg)}
                      surface={NAME_PAINT_SURFACES.chat}
                      className="chat-line-name"
                      fallbackClassName={`chat-line-name--c${usernameColorIndex(msg.username)}`}
                    />
                  )}
                  <span className="chat-line-separator" aria-hidden="true">{t('chat.lineSeparator')}</span>
                  <span className="chat-line-text">{msg.message}</span>
                  {msg.failed && (
                    <div className="chat-message-failed" role="alert">
                      <span>{t('chat.failed.notSent')}</span>
                      <button type="button" className="chat-message-failed-action" onClick={() => retryMessage(msg.id)}>
                        {t('chat.failed.retry')}
                      </button>
                      <button type="button" className="chat-message-failed-action" onClick={() => discardMessage(msg.id)}>
                        {t('chat.failed.remove')}
                      </button>
                    </div>
                  )}
                </div>
              )))}
              {!isStream && messages.map((msg) => (
                <div key={msg.id} className={`chat-message${msg.failed ? ' chat-message--failed' : ''}`}>
                  <div className="message-header">
                    <span className="message-username">{msg.username}</span>
                    <span className="message-timestamp">{formatChatTimestamp(msg.timestamp, format)}</span>
                  </div>
                  <div className="message-content">{msg.message}</div>
                  {msg.failed && (
                    <div className="chat-message-failed" role="alert">
                      <span>{t('chat.failed.notSent')}</span>
                      <button type="button" className="chat-message-failed-action" onClick={() => retryMessage(msg.id)}>
                        {t('chat.failed.retry')}
                      </button>
                      <button type="button" className="chat-message-failed-action" onClick={() => discardMessage(msg.id)}>
                        {t('chat.failed.remove')}
                      </button>
                    </div>
                  )}
                  {canModerate && msg.id && !String(msg.id).startsWith('local-') && (
                    <button
                      type="button"
                      className="chat-delete-button"
                      onClick={() => deleteMessage(msg.id)}
                      aria-label={t('chat.deleteMessageFrom', { username: msg.username })}
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              ))}
              {typingUsers.length > 0 && (
                <div className="chat-typing-indicator">
                  {t('chat.typing', { count: typingUsers.length, users: formatTypingUsers(typingUsers, format) })}
                </div>
              )}
            </div>
          </div>

          {pinned}

          {isStream && tryOnPanel}

          {!signedIn ? (
            <ChatSignInPrompt />
          ) : (
          <form className="chat-input-form" onSubmit={handleSendMessage}>
            <div className="chat-input-container">
              <input
                type="text"
                className="chat-input"
                placeholder={
                  isOffline
                    ? t('chat.composer.placeholderOffline')
                    : isCockpit ? t('chat.composer.placeholderCockpit')
                      : isStream ? t('chat.composer.placeholderStream') : t('chat.composer.placeholder')
                }
                aria-label={t('chat.composer.messageLabel')}
                value={message}
                onChange={handleMessageChange}
                maxLength={500}
                disabled={isOffline}
              />
              <button
                type="submit"
                className="chat-send-button"
                disabled={isOffline || !message.trim()}
                title={t('chat.composer.send')}
                aria-label={isStream ? undefined : t('chat.composer.send')}
              >
                {isCockpit ? t('chat.composer.sendShort') : isStream ? t('chat.composer.sendStream') : <FaPaperPlane />}
              </button>
            </div>
          </form>
          )}

          {isStream && profileCard && (
            <ShopNavigator render={(openShop) => (
            <ListenerProfileCard
              key={profileCard.lineId}
              userId={profileCard.userId}
              bandId={bandId}
              fallbackName={profileCard.username}
              onClose={() => setProfileCard(null)}
              onSeeWhatTheySee={onSeeWhatTheySee
                ? (card) => {
                  onSeeWhatTheySee(card);
                  setProfileCard(null);
                }
                : null}
              onSeeAll={ownCard
                ? (onSeeAll ? (card) => {
                  onSeeAll(card);
                  setProfileCard(null);
                } : null)
                : () => {
                  setProfileCard(null);
                  openShop();
                }}
              seeAllOpensShop={!ownCard}
              onTryOn={onTryOn
                ? (card) => {
                  onTryOn(card);
                  setProfileCard(null);
                }
                : null}
            />
            )} />
          )}
        </>
      )}
    </div>
  );
};

export default ChatSidebar;
